import React, { useEffect, useState, useCallback } from "react";
import { tasksApi } from "@api/tasks";
import { statsApi } from "@api/stats";

// Форматирование секунд -> "8ч 32м 18с"
function formatDuration(sec) {
  if (sec == null || sec <= 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts = [];
  if (h) parts.push(`${h}ч`);
  if (m) parts.push(`${m}м`);
  parts.push(`${s}с`);
  return parts.join(" ");
}

function fmt(val) {
  return val != null && val !== 0 ? Number(val).toLocaleString("ru-RU") : val === 0 ? "0" : "—";
}

function calcAvg(subscriptionsToday, subscribedAccounts) {
  if (!subscriptionsToday || !subscribedAccounts) return null;
  return (subscriptionsToday / subscribedAccounts).toFixed(1);
}

function getWeekNumber(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  const startOfYear = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
}

function getDayOfMonth(dateStr) {
  return new Date(dateStr + "T12:00:00").getDate();
}

// Текущая дата в формате YYYY-MM-DD по московскому времени (UTC+3)
function getTodayMSK() {
  const msk = new Date(Date.now() + 3 * 60 * 60 * 1000);
  return msk.toISOString().slice(0, 10);
}

export default function TaskStatsModal({ open, onClose, taskUid, taskName }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [liveToday, setLiveToday] = useState(null); // живые данные за сегодня
  const [totalProgress, setTotalProgress] = useState(null); // реальные данные из account_groups

  const load = useCallback(async () => {
    if (!taskUid) return;
    setLoading(true);
    try {
      const [data, todayData, progressData] = await Promise.allSettled([
        tasksApi.history(taskUid),
        statsApi.todayByTask([taskUid]),
        statsApi.totalProgress(taskUid),
      ]);

      setHistory(data.status === "fulfilled" ? data.value?.history || [] : []);
      setLiveToday(todayData.status === "fulfilled" ? todayData.value?.tasks?.[String(taskUid)] || null : null);
      setTotalProgress(progressData.status === "fulfilled" ? progressData.value : null);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [taskUid]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  // Автообновление каждые 30 секунд пока открыто
  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const todayStr = getTodayMSK();
  // Проверяем, есть ли уже сохранённая запись за сегодня
  const hasSavedToday = history.some((r) => r.date === todayStr);

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div className="absolute inset-0 grid place-items-center px-4 py-6">
        <div className="w-full max-w-[1000px] max-h-[90vh] flex flex-col bg-white rounded-[18px] border border-[#C8C8C8] shadow-[0_12px_28px_rgba(0,0,0,0.12)]">

          {/* Header */}
          <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-[#E5E7EB]">
            <div>
              <h3 className="text-[18px] font-semibold text-[#111827]">
               <i className="fa-solid fa-chart-simple text-[#111827]" /> Статистика задачи
              </h3>
              <p className="mt-1 text-[12px] text-[#6B7280]">
                {taskName ? `${taskName} • ` : ""}последние 30 дней
              </p>
            </div>

            <button
              type="button"
              className="w-9 h-9 rounded-full bg-[#F3F4F6] grid place-items-center hover:bg-[#EDEFF3] transition flex-shrink-0"
              onClick={onClose}
            >
              <i className="fa-solid fa-xmark text-[#111827]" />
            </button>
          </div>

          {/* Панель реального прогресса */}
          {totalProgress && (
            <div className="px-6 py-3 bg-[#F0F7FF] border-b border-[#C7DFFF] flex items-center gap-6 flex-wrap text-[13px]">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-database text-[#2B7CFF]" />
                <span className="text-[#374151] font-medium">Всего подписок (реально):</span>
                <span className="font-bold text-[#2B7CFF] text-[15px]">
                  {Number(totalProgress.totalSubscriptions).toLocaleString("ru-RU")}
                </span>
              </div>
              <div className="text-[#6B7280]">
                из {Number(totalProgress.totalTarget).toLocaleString("ru-RU")} 
                ({totalProgress.accountsInTask} акк × {totalProgress.groupsInTask} групп)
              </div>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#2B7CFF] rounded-full"
                    style={{ width: `${totalProgress.progressPct}%` }}
                  />
                </div>
                <span className="font-semibold text-[#2B7CFF]">{totalProgress.progressPct}%</span>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="overflow-auto flex-1">
            {loading && (
              <div className="p-8 text-center text-sm text-[#6B7280]">Загрузка...</div>
            )}

            {!loading && history.length === 0 && !liveToday && (
              <div className="p-8 text-center text-sm text-[#6B7280]">
                Нет данных за последние 30 дней.
                <br />
                <span className="text-[11px] text-[#9CA3AF]">
                  Статистика сохраняется автоматически при ежедневном сбросе в 3:00 МСК.
                </span>
              </div>
            )}

            {!loading && (history.length > 0 || liveToday) && (
              <table className="w-full text-[13px] border-collapse">
                <thead>
                  <tr className="sticky top-0 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <th className="px-4 py-3 text-left font-semibold text-[#6B7280] whitespace-nowrap">День</th>
                    <th className="px-4 py-3 text-center font-semibold text-[#6B7280] whitespace-nowrap">
                      Статистика<br /><span className="font-normal text-[11px]">(валидные)</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-[#6B7280] whitespace-nowrap">
                      Валидные<br /><span className="font-normal text-[11px]">(подписались)</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-[#6B7280] whitespace-nowrap">
                      Подписок<br /><span className="font-normal text-[11px]">за сегодня</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-[#6B7280] whitespace-nowrap">
                      Подписок<br /><span className="font-normal text-[11px]">всего</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-[#6B7280]">Среднее / аккаунт</th>
                    <th className="px-4 py-3 text-center font-semibold text-[#6B7280] whitespace-nowrap">Время работы</th>
                  </tr>
                </thead>
                <tbody>
                  {/* ✅ Живая строка "сегодня" — всегда актуальные данные */}
                  {liveToday && !hasSavedToday && (
                    <tr className="border-b border-[#E8F0FE] bg-[#F0F7FF]">
                      <td className="px-4 py-3 font-semibold text-[#2B7CFF]">
                        {getDayOfMonth(todayStr)}
                        <span className="ml-1 text-[11px] font-normal text-[#6B9EFF]">
                          {todayStr.slice(5)}
                        </span>
                        <span className="ml-1.5 text-[10px] bg-[#2B7CFF] text-white rounded-full px-1.5 py-0.5">live</span>
                      </td>
                      <td className="px-4 py-3 text-center text-[#374151]">—</td>
                      <td className="px-4 py-3 text-center text-[#374151]">
                        {fmt(liveToday.uniqueAccountsToday)}
                      </td>
                      <td className="px-4 py-3 text-center text-[#374151] font-medium">
                        {fmt(liveToday.totalSubscriptionsToday)}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-[#2B7CFF]">
                        {totalProgress ? Number(totalProgress.totalSubscriptions).toLocaleString("ru-RU") : "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-[#374151]">
                        {liveToday.avgSubscriptionsPerAccountToday
                          ? `${liveToday.avgSubscriptionsPerAccountToday} подп.`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-center text-[#6B7280] text-[11px] italic">обновляется...</td>
                    </tr>
                  )}

                  {history.map((row, i) => {
                    const avg = calcAvg(row.subscriptionsToday, row.subscribedAccounts);
                    const week = avg ? getWeekNumber(row.date) : null;
                    const isToday = row.date === todayStr;
                    const isEven = i % 2 === 0;

                    return (
                      <tr
                        key={row.date}
                        className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition ${
                          isToday ? "bg-[#F0F7FF]" : isEven ? "bg-white" : "bg-[#FAFAFA]"
                        }`}
                      >
                        <td className="px-4 py-3 font-semibold text-[#111827]">
                          {getDayOfMonth(row.date)}
                          <span className="ml-1 text-[11px] font-normal text-[#9CA3AF]">
                            {row.date.slice(5)}
                          </span>
                          {isToday && (
                            <span className="ml-1.5 text-[10px] bg-[#E8F0FE] text-[#2B7CFF] rounded-full px-1.5 py-0.5">сег.</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-[#374151]">{fmt(row.validAccounts)}</td>
                        <td className="px-4 py-3 text-center text-[#374151]">{fmt(row.subscribedAccounts)}</td>
                        <td className="px-4 py-3 text-center text-[#374151]">{fmt(row.subscriptionsToday)}</td>
                        <td className="px-4 py-3 text-center font-semibold text-[#2B7CFF]">
                          {fmt(row.totalSubscriptions)}
                        </td>
                        <td className="px-4 py-3 text-center text-[#374151]">
                          {avg ? `${avg} подп., ${week} скорость` : "—"}
                        </td>
                        <td className="px-4 py-3 text-center text-[#1F7A3A] font-medium">
                          {formatDuration(row.workDurationSeconds)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[#E5E7EB]">
            <span className="text-[11px] text-[#9CA3AF]">
              {loading ? "Обновление..." : `Записей: ${history.length}`}
            </span>
            <button
              type="button"
              onClick={load}
              className="h-8 px-4 rounded-full bg-[#F3F4F6] text-[#374151] text-[12px] font-medium hover:bg-[#EDEFF3] transition flex items-center gap-2"
            >
              <i className="fa-solid fa-rotate-right text-[11px]" />
              Обновить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
